import React, { useCallback, useEffect, useRef, useState } from "react";
import { Line, Pie } from "react-chartjs-2";
import Chart from "chart.js/auto";
import { api } from "./apiClient";
import { formatApiError } from "./apiError";
import { useAuth } from "./AuthContext";
import { useServerDebug } from "./DebugContext";

const initialFilters = {
  start: "",
  end: "",
  status: "",
  category: "",
  username: "",
};

/** API must return a JSON array; `{}` or other shapes would break `.map`. */
function asArray(value) {
  return Array.isArray(value) ? value : [];
}

/** Large CSVs: POST each part under reverse-proxy body limits (413). */
const DEFAULT_CHUNK_BYTES = 256 * 1024;

/** Max raw file bytes per chunk (multipart adds ~2–8% + boundaries; stay under nginx limit). */
function budgetFileBytesPerChunk(maxFromServer) {
  const cap = maxFromServer || DEFAULT_CHUNK_BYTES;
  const fromEnv = Number(process.env.REACT_APP_UPLOAD_CHUNK_FILE_BYTES);
  if (Number.isFinite(fromEnv) && fromEnv >= 4096) {
    return Math.min(Math.floor(fromEnv), cap);
  }
  return Math.max(4096, Math.floor(cap * 0.62));
}

/**
 * End index (exclusive) for the next chunk starting at i.
 * Prefer newline breaks for CSV; then avoid splitting a UTF-8 codepoint.
 */
function nextChunkEnd(u8, i, maxLen) {
  const n = u8.length;
  if (i >= n) return n;
  let end = Math.min(i + maxLen, n);
  if (end >= n) return n;
  let foundNl = false;
  for (let k = end - 1; k > i; k -= 1) {
    if (u8[k] === 0x0a) {
      end = k + 1;
      foundNl = true;
      break;
    }
  }
  if (!foundNl) {
    while (end > i && (u8[end] & 0xc0) === 0x80) {
      end -= 1;
    }
    if (end === i) {
      end = Math.min(i + maxLen, n);
    }
  }
  return end;
}

function buildUploadSlices(u8, maxEach) {
  const slices = [];
  let i = 0;
  while (i < u8.length) {
    const j = nextChunkEnd(u8, i, maxEach);
    if (j === i) {
      slices.push(u8.subarray(i, i + 1));
      i += 1;
    } else {
      slices.push(u8.subarray(i, j));
      i = j;
    }
  }
  return slices;
}

async function uploadCsvInChunks(apiClient, file, onProgress) {
  const { data: init } = await apiClient.post("/upload-csv/init");
  const uploadId = init.upload_id;
  const serverMax = init.max_chunk_bytes || DEFAULT_CHUNK_BYTES;
  const fileBudget = budgetFileBytesPerChunk(serverMax);
  const ab = await file.arrayBuffer();
  const u8 = new Uint8Array(ab);
  const parts = buildUploadSlices(u8, fileBudget);
  const totalChunks = parts.length;
  for (let i = 0; i < totalChunks; i += 1) {
    const blob = new Blob([parts[i]], { type: "application/octet-stream" });
    const fd = new FormData();
    fd.append("upload_id", uploadId);
    fd.append("chunk_index", String(i));
    fd.append("total_chunks", String(totalChunks));
    fd.append("file", blob, file.name);
    await apiClient.post("/upload-csv/chunk", fd);
    onProgress(i + 1, totalChunks);
  }
  const { data: done } = await apiClient.post("/upload-csv/complete", {
    upload_id: uploadId,
    total_chunks: totalChunks,
    filename: file.name,
  });
  return done;
}

export default function FinanceDashboard({
  onOpenCuReports,
  onOpenAdmin,
  cuReportsHref,
  adminHref,
}) {
  const { user, logout, hasPermission } = useAuth();
  const serverDebug = useServerDebug();
  const showAdminNav = ["admin.users", "admin.roles", "admin.permissions"].some(hasPermission);
  const [data, setData] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [stats, setStats] = useState({ total: 0, amount: 0, avg_amount: 0 });
  const [lineChart, setLineChart] = useState({ labels: [], datasets: [] });
  const [pieChart, setPieChart] = useState({ labels: [], datasets: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadMsg, setUploadMsg] = useState(null);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const filterParams = useCallback((f) => {
    const p = {};
    if (f.start) p.start_date = f.start;
    if (f.end) p.end_date = f.end;
    if (f.status.trim()) p.status = f.status.trim();
    if (f.category.trim()) p.category = f.category.trim();
    if (f.username.trim()) p.username = f.username.trim();
    return p;
  }, []);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    const params = filterParams(filtersRef.current);
    try {
      const [statsRes, dataRes, lineRes, pieRes] = await Promise.all([
        api.get("/api/stats", { params }),
        api.get("/api/data", { params: { ...params, limit: 500, offset: 0 } }),
        api.get("/api/chart-data", { params }),
        api.get("/api/category-distribution", { params }),
      ]);
      setStats(
        statsRes.data && typeof statsRes.data === "object" && !Array.isArray(statsRes.data)
          ? statsRes.data
          : { total: 0, amount: 0, avg_amount: 0 },
      );
      setData(asArray(dataRes.data));

      const line = asArray(lineRes.data);
      setLineChart({
        labels: line.map((r) => r.day),
        datasets: [
          {
            label: "Transactions per day",
            data: line.map((r) => r.total),
            borderColor: "rgb(59, 130, 246)",
            backgroundColor: "rgba(59, 130, 246, 0.15)",
            fill: true,
            tension: 0.2,
          },
        ],
      });

      const pie = asArray(pieRes.data);
      const palette = [
        "#3b82f6",
        "#10b981",
        "#f59e0b",
        "#ef4444",
        "#8b5cf6",
        "#ec4899",
        "#06b6d4",
        "#84cc16",
      ];
      setPieChart({
        labels: pie.map((r) => r.name || "—"),
        datasets: [
          {
            data: pie.map((r) => r.total),
            backgroundColor: pie.map((_, i) => palette[i % palette.length]),
          },
        ],
      });
    } catch (e) {
      setError(formatApiError(e, serverDebug));
    } finally {
      setLoading(false);
    }
  }, [filterParams]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleCsv = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setUploadMsg("Choose a .csv file.");
      return;
    }
    setUploadBusy(true);
    setUploadMsg(null);
    setError(null);
    try {
      setUploadMsg("Starting chunked upload…");
      const done = await uploadCsvInChunks(api, file, (cur, total) => {
        setUploadMsg(`Uploading chunk ${cur} / ${total}…`);
      });
      setUploadMsg(
        done?.message
          ? `${done.message} (${done.rows_inserted ?? "?"} rows)`
          : "Upload complete.",
      );
      await fetchDashboard();
    } catch (err) {
      setUploadMsg(formatApiError(err, serverDebug));
    } finally {
      setUploadBusy(false);
    }
  };

  const fmtMoney = (n) =>
    n == null || Number.isNaN(Number(n))
      ? "—"
      : Number(n).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

  const fmtTs = (v) => {
    if (!v) return "—";
    return String(v).replace("T", " ").slice(0, 19);
  };

  return (
    <div style={{ display: "flex", fontFamily: "system-ui, sans-serif", minHeight: "100vh" }}>
      <aside
        style={{
          width: "240px",
          background: "#0f172a",
          color: "#e2e8f0",
          padding: "24px 20px",
          flexShrink: 0,
        }}
      >
        <h1 style={{ fontSize: "1.25rem", margin: "0 0 8px", fontWeight: 600 }}>
          Finance dashboard
        </h1>
        <p style={{ margin: 0, fontSize: "0.875rem", color: "#94a3b8" }}>
          Transactions, filters, and CSV import.
        </p>
        {typeof onOpenCuReports === "function" && hasPermission("cu.reports.view") && (
          <a
            href={cuReportsHref || "/cu-reports"}
            onClick={(e) => {
              e.preventDefault();
              onOpenCuReports();
            }}
            style={{
              display: "block",
              marginTop: "16px",
              width: "100%",
              padding: "10px 12px",
              borderRadius: "8px",
              border: "1px solid #334155",
              background: "linear-gradient(135deg, #0d9488 0%, #0f766e 100%)",
              color: "#f0fdfa",
              fontSize: "0.8125rem",
              fontWeight: 600,
              cursor: "pointer",
              textAlign: "center",
              textDecoration: "none",
              boxSizing: "border-box",
            }}
          >
            Credit union time survey →
          </a>
        )}
        {typeof onOpenAdmin === "function" && showAdminNav && (
          <a
            href={adminHref || "/admin/users"}
            onClick={(e) => {
              e.preventDefault();
              onOpenAdmin();
            }}
            style={{
              display: "block",
              marginTop: "10px",
              width: "100%",
              padding: "10px 12px",
              borderRadius: "8px",
              border: "1px solid #334155",
              background: "rgba(51, 65, 85, 0.6)",
              color: "#e2e8f0",
              fontSize: "0.8125rem",
              fontWeight: 600,
              cursor: "pointer",
              textAlign: "center",
              textDecoration: "none",
              boxSizing: "border-box",
            }}
          >
            Users & roles →
          </a>
        )}
        <div style={{ marginTop: "18px", fontSize: "0.75rem", color: "#64748b" }}>
          {user?.username ? `${user.username} · ${user.email}` : user?.email}
          <button
            type="button"
            onClick={logout}
            style={{
              display: "block",
              marginTop: "8px",
              width: "100%",
              padding: "8px",
              borderRadius: "8px",
              border: "1px solid #475569",
              background: "transparent",
              color: "#94a3b8",
              cursor: "pointer",
              fontSize: "0.75rem",
            }}
          >
            Sign out
          </button>
        </div>
        <div
          style={{
            marginTop: "28px",
            paddingTop: "20px",
            borderTop: "1px solid #334155",
            fontSize: "0.8rem",
            color: "#64748b",
          }}
        >
          <p style={{ margin: "0 0 8px" }}>CSV headers (exact names):</p>
          <code style={{ display: "block", whiteSpace: "pre-wrap", lineHeight: 1.4 }}>
            Account Serial, Monetary Serial, Type Serial, Branch Suffix, Username,
            Category, Monetary Principal, Posting Time, Description, STATUS,
            MONETARY_SERIAL, SHARE_LOAN_ID, Source, InstrumentID
          </code>
        </div>
      </aside>

      <main style={{ flex: 1, padding: "24px 28px", background: "#f8fafc" }}>
        <header
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "16px",
            marginBottom: "24px",
          }}
        >
          <h2 style={{ margin: 0, fontSize: "1.5rem", color: "#0f172a" }}>Overview</h2>
          <label
            style={{
              marginLeft: "auto",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              cursor: uploadBusy ? "wait" : "pointer",
            }}
          >
            <span
              style={{
                padding: "8px 14px",
                background: "#2563eb",
                color: "#fff",
                borderRadius: "8px",
                fontSize: "0.875rem",
                fontWeight: 500,
              }}
            >
              {uploadBusy ? "Uploading…" : "Upload CSV"}
            </span>
            <input
              type="file"
              accept=".csv,text/csv"
              style={{ display: "none" }}
              disabled={uploadBusy}
              onChange={handleCsv}
            />
          </label>
        </header>

        {uploadMsg && (
          <pre
            style={{
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              padding: "12px",
              fontSize: "0.8rem",
              maxHeight: "160px",
              overflow: "auto",
              marginBottom: "16px",
            }}
          >
            {uploadMsg}
          </pre>
        )}

        <section
          style={{
            background: "#fff",
            borderRadius: "12px",
            padding: "16px",
            marginBottom: "20px",
            border: "1px solid #e2e8f0",
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" }}>
            <input type="date" name="start" value={filters.start} onChange={handleChange} />
            <input type="date" name="end" value={filters.end} onChange={handleChange} />
            <input
              placeholder="Status"
              name="status"
              value={filters.status}
              onChange={handleChange}
              style={inputStyle}
            />
            <input
              placeholder="Category"
              name="category"
              value={filters.category}
              onChange={handleChange}
              style={inputStyle}
            />
            <input
              placeholder="Username"
              name="username"
              value={filters.username}
              onChange={handleChange}
              style={inputStyle}
            />
            <button
              type="button"
              onClick={fetchDashboard}
              disabled={loading}
              style={btnPrimary}
            >
              {loading ? "Loading…" : "Apply filters"}
            </button>
          </div>
        </section>

        {error && (
          <div
            style={{
              background: "#fef2f2",
              color: "#b91c1c",
              padding: "12px 16px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            {String(error)}
          </div>
        )}

        <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "24px" }}>
          <Kpi title="Total transactions" value={stats.total} />
          <Kpi title="Total principal" value={fmtMoney(stats.amount)} />
          <Kpi title="Average principal" value={fmtMoney(stats.avg_amount)} />
        </div>

        <div style={{ display: "flex", gap: "20px", flexWrap: "wrap", marginBottom: "24px" }}>
          <div
            style={{
              flex: "1 1 420px",
              background: "#fff",
              borderRadius: "12px",
              padding: "16px",
              border: "1px solid #e2e8f0",
              minHeight: "280px",
            }}
          >
            {lineChart.labels.length > 0 ? (
              <Line
                data={lineChart}
                options={{
                  responsive: true,
                  plugins: { legend: { display: true } },
                  scales: { x: { ticks: { maxRotation: 45 } } },
                }}
              />
            ) : (
              <p style={{ color: "#64748b", margin: "40px 0", textAlign: "center" }}>
                No daily transaction data for the current filters.
              </p>
            )}
          </div>
          <div
            style={{
              flex: "0 1 340px",
              background: "#fff",
              borderRadius: "12px",
              padding: "16px",
              border: "1px solid #e2e8f0",
              minHeight: "280px",
            }}
          >
            {pieChart.labels.length > 0 ? (
              <Pie
                data={pieChart}
                options={{
                  responsive: true,
                  plugins: { legend: { position: "right" } },
                }}
              />
            ) : (
              <p style={{ color: "#64748b", margin: "40px 0", textAlign: "center" }}>
                No category breakdown for the current filters.
              </p>
            )}
          </div>
        </div>

        <div
          style={{
            background: "#fff",
            borderRadius: "12px",
            border: "1px solid #e2e8f0",
            overflow: "auto",
            maxHeight: "520px",
          }}
        >
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8125rem" }}>
            <thead>
              <tr style={{ background: "#f1f5f9", textAlign: "left" }}>
                <th style={th}>Posted</th>
                <th style={th}>User</th>
                <th style={th}>Principal</th>
                <th style={th}>Category</th>
                <th style={th}>Status</th>
                <th style={th}>Source</th>
                <th style={th}>Account</th>
                <th style={th}>Monetary #</th>
                <th style={th}>Instrument</th>
                <th style={th}>Description</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, i) => (
                <tr key={row.id ?? i} style={{ borderTop: "1px solid #e2e8f0" }}>
                  <td style={td}>{fmtTs(row.posting_time)}</td>
                  <td style={td}>{row.username || "—"}</td>
                  <td style={td}>{fmtMoney(row.monetary_principal)}</td>
                  <td style={td}>{row.category || "—"}</td>
                  <td style={td}>{row.status || "—"}</td>
                  <td style={td}>{row.source || "—"}</td>
                  <td style={td}>{row.account_serial ?? "—"}</td>
                  <td style={td}>{row.monetary_serial ?? "—"}</td>
                  <td style={td}>{row.instrument_id || "—"}</td>
                  <td style={tdMuted}>{row.description || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.length === 0 && !loading && (
            <p style={{ padding: "24px", color: "#64748b", margin: 0 }}>No rows match.</p>
          )}
        </div>
      </main>
    </div>
  );
}

function Kpi({ title, value }) {
  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #e2e8f0",
        borderRadius: "12px",
        padding: "18px 22px",
        minWidth: "160px",
      }}
    >
      <div style={{ fontSize: "0.75rem", color: "#64748b", marginBottom: "6px" }}>{title}</div>
      <div style={{ fontSize: "1.35rem", fontWeight: 600, color: "#0f172a" }}>{value}</div>
    </div>
  );
}

const inputStyle = { padding: "8px 10px", borderRadius: "6px", border: "1px solid #cbd5e1" };
const btnPrimary = {
  padding: "8px 16px",
  borderRadius: "8px",
  border: "none",
  background: "#0f172a",
  color: "#fff",
  cursor: "pointer",
  fontWeight: 500,
};
const th = { padding: "10px 12px", fontWeight: 600, color: "#475569" };
const td = { padding: "10px 12px", verticalAlign: "top" };
const tdMuted = { ...td, color: "#475569", maxWidth: "280px" };
